import { motion, useInView } from "framer-motion";
import { useRef } from "react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

const principles = [
  {
    quote: "Optimize for operational clarity.",
    context: "Systems should make complexity legible, not just functional.",
  },
  {
    quote: "Good systems reduce workflow friction.",
    context: "Every abstraction should earn its place through measurable improvement.",
  },
  {
    quote: "Scalable architecture is usually incremental.",
    context: "The best migrations are invisible to users and gradual for teams.",
  },
  {
    quote: "AI is most valuable when embedded into workflows.",
    context: "Intelligence without integration is just noise.",
  },
  {
    quote: "Product quality emerges from thoughtful tradeoffs.",
    context: "Speed, flexibility, and reliability are always in tension. Navigate intentionally.",
  },
];

export const Philosophy = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="philosophy" ref={ref} data-testid="philosophy-section" className="py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial="initial"
          animate={isInView ? "animate" : "initial"}
          transition={{ staggerChildren: 0.1 }}
          className="mb-16"
        >
          <motion.span
            variants={fadeUp}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="inline-block text-xs font-mono text-[#38bdf8] tracking-wider uppercase mb-4"
          >
            Working Style
          </motion.span>
          <motion.h2
            variants={fadeUp}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            data-testid="philosophy-heading"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#EDEDED] tracking-tight"
          >
            Engineering philosophy.
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {principles.map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="bg-[#121212] border border-white/[0.08] rounded-xl p-6 hover:border-white/[0.15] transition-all duration-300"
              data-testid={`philosophy-card-${i}`}
            >
              <blockquote className="font-heading text-lg font-semibold text-[#EDEDED] mb-3 tracking-tight leading-snug">
                "{p.quote}"
              </blockquote>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">
                {p.context}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
